import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"

import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { ContactsFilterNegationKey } from "./contacts-filter-state"
import type { FilterType } from "./filter-popover-items/types"
import type { FilterChip } from "@/components/filters"
import { FilterControlChips } from "@/components/filters"
import { SOURCE_OPTIONS } from "@/components/filters/source-options"
import { useContactsFilterState } from "./contacts-filter-state"
import { CompanyFilterItems } from "./filter-popover-items/company-filter-items"
import { SourceFilterItems } from "./filter-popover-items/source-filter-items"
import { SubscriptionFilterItems } from "./filter-popover-items/subscription-filter-items"

const CUSTOM_FILTER_LABELS: FilterType[] = ["Companies"]

export function ContactsFilterControl() {
	const {
		companyIds,
		subscribedStates,
		sources,
		negations,
		setNegation,
		clearCompanies,
		clearSubscribedStates,
		clearSources,
	} = useContactsFilterState()
	const singleCompanyId = companyIds.length === 1 ? companyIds[0] : null
	const [singleCompany] = useQuery(queries.companyById(singleCompanyId ?? ""), {
		enabled: !!singleCompanyId,
	})

	const [query, setQuery] = useState("")

	const negationProps = (
		key: ContactsFilterNegationKey,
		valueCount: number,
	) => ({
		valueCount,
		isNegated: negations[key] ?? false,
		onNegatedChange: (isNegated: boolean) => {
			setNegation(key, isNegated)
		},
	})

	const onFilterItemSelect = () => {
		setTimeout(() => setQuery(""), 100)
	}

	const renderEditor = (label: FilterType) => (
		<Command shouldFilter={!CUSTOM_FILTER_LABELS.includes(label)}>
			<CommandInput
				autoFocus
				placeholder="Filter..."
				value={query}
				onValueChange={setQuery}
			/>
			<CommandList className="max-h-[70dvh]">
				<CommandEmpty>Nothing found.</CommandEmpty>
				<CommandGroup>
					{label === "Companies" && (
						<CompanyFilterItems query={query} onSelect={onFilterItemSelect} />
					)}
					{label === "Subscription" && (
						<SubscriptionFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "CreatedBy" && (
						<SourceFilterItems onSelect={onFilterItemSelect} />
					)}
				</CommandGroup>
			</CommandList>
		</Command>
	)

	const chips = useMemo<FilterChip[]>(() => {
		const result: FilterChip[] = []
		if (companyIds.length > 0) {
			result.push({
				key: "Companies",
				label: "Company",
				...negationProps("companyIds", companyIds.length),
				valueText:
					companyIds.length === 1
						? (singleCompany?.name ??
							(companyIds[0] === null ? "No company" : ""))
						: companyIds.includes(null)
							? `No company and ${companyIds.length - 1} compan${
									companyIds.length - 1 > 1 ? "ies" : "y"
								}`
							: `${companyIds.length} companies`,
				onClear: clearCompanies,
				popoverContent: renderEditor("Companies"),
			})
		}
		if (subscribedStates.length > 0) {
			result.push({
				key: "Subscription",
				label: "Subscription",
				...negationProps("subscribedStates", subscribedStates.length),
				valueText:
					subscribedStates.length === 1
						? subscribedStates[0]
							? "Subscribed"
							: "Unsubscribed"
						: `${subscribedStates.length} states`,
				onClear: clearSubscribedStates,
				popoverContent: renderEditor("Subscription"),
			})
		}
		if (sources.length > 0) {
			result.push({
				key: "CreatedBy",
				label: "Synced with",
				...negationProps("sources", sources.length),
				valueText:
					sources.length === 1
						? (SOURCE_OPTIONS.find((o) => o.value === sources[0])?.label ??
							sources[0] ??
							"")
						: `${sources.length} sources`,
				onClear: clearSources,
				popoverContent: renderEditor("CreatedBy"),
			})
		}
		return result
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyIds, subscribedStates, sources, negations, singleCompany, query])

	return <FilterControlChips chips={chips} />
}
